import { Log } from '@fanfanlo';
import { matrixClient } from '../client';
import { roomData } from './data';
import { IRoomSettings } from './types';

const fileLogger = new Log(false, 'im.client.room.roomSettingsService');
fileLogger.pause = true; // 全局 logger 默认关闭

export interface IRoomPermissions {
    // 发送消息的最低权限级别
    sendMessages?: number;
    // 邀请用户的最低权限级别
    invite?: number;
    // 踢出用户的最低权限级别
    kick?: number;
    // 封禁用户的最低权限级别
    ban?: number;
    // 修改房间设置的最低权限级别
    roomSettings?: number;
}

export interface IRoomPrivacySettings {
    isPublic: boolean;
    isEncrypted: boolean;
    allowGuestJoin: boolean;
}

export interface IUpdateResult {
    success: boolean;
    error?: string;
}

export const roomSettingsService = {
    // 检查 Matrix 客户端是否已初始化
    checkClient() {
        const client = matrixClient.client;
        if (!client) {
            throw new Error('Matrix client not initialized');
        }
        return client;
    },

    // 获取房间状态事件
    async getRoomStateEvent(room: any, eventType: string, stateKey = '') {
        const logger = new Log(false, 'im.client.room.roomSettingsService.getRoomStateEvent');
        // logger.pause = true;
        try {
            // 如果 room 是 Promise，等待它解析
            const resolvedRoom = await (room instanceof Promise ? room : room);
            
            logger.log('Room 对象:', {
                room: resolvedRoom,
                hasCurrentState: !!resolvedRoom?.currentState,
                hasGetState: typeof resolvedRoom?.getState === 'function',
                hasGetLiveTimeline: typeof resolvedRoom?.getLiveTimeline === 'function',
                methods: Object.keys(resolvedRoom || {}),
                prototype: resolvedRoom ? Object.getPrototypeOf(resolvedRoom) : null
            });

            if (!resolvedRoom) {
                logger.warn('Room 对象为空');
                return null;
            }

            // 使用新的 API
            if (resolvedRoom.currentState?.getStateEvents) {
                logger.log('使用 currentState.getStateEvents');
                return resolvedRoom.currentState.getStateEvents(eventType, stateKey);
            }

            // 尝试获取状态事件的其他方法
            if (resolvedRoom.getStateEvents) {
                logger.log('使用 room.getStateEvents');
                return resolvedRoom.getStateEvents(eventType, stateKey);
            }

            if (resolvedRoom.state) {
                logger.log('使用 room.state');
                const events = resolvedRoom.state.getStateEvents?.(eventType, stateKey) || 
                              resolvedRoom.state.events?.[eventType]?.get?.(stateKey);
                if (events) return events;
            }

            logger.warn('无法获取房间状态事件，返回 null');
            return null;
        } catch (error) {
            logger.error('获取房间状态事件失败:', error);
            return null;
        }
    },

    // 检查权限
    async hasPermission(roomId: string, action: string) {
        // 不打印
        return (async () => {
            try {
                const client = this.checkClient();
                const powerLevels = await client.getStateEvent(roomId, 'm.room.power_levels', '');
                const userId = await client.getUserId();
                const userLevel = userId ? (powerLevels.users?.[userId] ?? powerLevels.users_default ?? 0) : (powerLevels.users_default ?? 0);
                const requiredLevel = powerLevels.state_default || 50;
                return userLevel >= requiredLevel;
            } catch {
                return false;
            }
        })();
    },

    // 基础设置
    async updateRoomName(roomId: string, name: string) {
        // 不打印
        const client = this.checkClient();
        await client.setRoomName(roomId, name);
    },

    async updateRoomAvatar(roomId: string, avatarUrl: string) {
        // 不打印
        if (!await this.hasPermission(roomId, 'state_default')) {
            throw new Error('没有修改房间头像的权限');
        }
        const client = this.checkClient();
        const content = { url: avatarUrl };
        await client.sendStateEvent(roomId, 'm.room.avatar' as any, content, '');
        const roomInfo = roomData.joinedRooms.find(r => r.roomId === roomId);
        if (roomInfo) {
            roomInfo.avatarUrl = avatarUrl;
        }
    },

    async updateRoomTopic(roomId: string, topic: string) {
        // 不打印
        if (!await this.hasPermission(roomId, 'state_default')) {
            throw new Error('没有修改房间简介的权限');
        }
        const client = this.checkClient();
        await client.setRoomTopic(roomId, topic);
    },

    // 权限管理
    async updateRoomPermissions(roomId: string, permissions: IRoomPermissions) {
        // 不打印
        if (!await this.hasPermission(roomId, 'state_default')) {
            throw new Error('没有修改房间权限的权限');
        }
        const client = this.checkClient();
        const powerLevels = await client.getStateEvent(roomId, 'm.room.power_levels', '');
        const newContent = {
            ...powerLevels,
            events: {
                ...powerLevels.events,
                'm.room.message': permissions.sendMessages ?? powerLevels.events?.['m.room.message'] ?? 0,
            },
            invite: permissions.invite ?? powerLevels.invite ?? 50,
            kick: permissions.kick ?? powerLevels.kick ?? 50,
            ban: permissions.ban ?? powerLevels.ban ?? 50,
            state_default: permissions.roomSettings ?? powerLevels.state_default ?? 50,
        };
        await client.sendStateEvent(roomId, 'm.room.power_levels' as any, newContent, '');
    },

    // 成员管理
    async kickMember(roomId: string, userId: string, reason?: string) {
        if (!await this.hasPermission(roomId, 'kick')) {
            throw new Error('没有踢出成员的权限');
        }

        await matrixClient.client.kick(roomId, userId, reason);
    },

    async banMember(roomId: string, userId: string, reason?: string) {
        if (!await this.hasPermission(roomId, 'ban')) {
            throw new Error('没有封禁成员的权限');
        }

        await matrixClient.client.ban(roomId, userId, reason);
    },

    async unbanMember(roomId: string, userId: string) {
        if (!await this.hasPermission(roomId, 'ban')) {
            throw new Error('没有解除封禁的权限');
        }

        await matrixClient.client.unban(roomId, userId);
    },

    // 隐私设置
    async updateRoomVisibility(roomId: string, isPublic: boolean) {
        // 不打印
        const client = this.checkClient();
        const visibility = isPublic ? ('public' as any) : ('private' as any);
        await client.setRoomDirectoryVisibility(roomId, visibility);
    },

    async updateGuestAccess(roomId: string, allowGuests: boolean) {
        // 不打印
        if (!await this.hasPermission(roomId, 'state_default')) {
            throw new Error('没有修改访客权限的权限');
        }
        const client = this.checkClient();
        const content = {
            guest_access: allowGuests ? 'can_join' : 'forbidden'
        };
        await client.sendStateEvent(roomId, 'm.room.guest_access' as any, content, '');
    },

    async updateEncryption(roomId: string, enable: boolean) {
        // 不打印
        if (!await this.hasPermission(roomId, 'state_default')) {
            throw new Error('没有修改加密设置的权限');
        }
        const client = this.checkClient();
        if (enable) {
            const content = {
                algorithm: 'm.megolm.v1.aes-sha2'
            };
            await client.sendStateEvent(roomId, 'm.room.encryption' as any, content, '');
        }
    },

    // 获取当前设置
    async getRoomSettings(roomId: string): Promise<IRoomSettings> {
        const logger = new Log(false, 'im.client.room.roomSettingsService.getRoomSettings');
        logger.pause = false; // 只打印 getRoomSettings 入口
        logger.log('获取房间设置:', roomId);
        try {
            const client = this.checkClient();
            const room = await client.getRoom(roomId);
            if (!room) {
                throw new Error('房间不存在');
            }
            // 获取房间名
            const name = room.name;
            // 获取房间主题
            let topic: string | null = null;
            logger.log('即将请求 m.room.topic');
            try {
                const topicEvent = await client.getStateEvent(roomId, 'm.room.topic', '');
                topic = topicEvent?.topic ?? null;
                logger.log('m.room.topic 返回:', topic);
            } catch {}
            // 获取权限
            let powerLevels: any = {};
            logger.log('即将请求 m.room.power_levels');
            try {
                powerLevels = await client.getStateEvent(roomId, 'm.room.power_levels', '');
                logger.log('m.room.power_levels 返回:', powerLevels);
            } catch {}
            // 获取隐私
            let isEncrypted = false;
            let allowGuestJoin = false;
            let isPublic = false;
            logger.log('即将请求 m.room.encryption');
            try {
                const encryption = await client.getStateEvent(roomId, 'm.room.encryption', '');
                isEncrypted = !!encryption;
                logger.log('m.room.encryption 返回:', encryption);
            } catch {}
            logger.log('即将请求 m.room.guest_access');
            try {
                const guestAccess = await client.getStateEvent(roomId, 'm.room.guest_access', '');
                allowGuestJoin = guestAccess?.guest_access === 'can_join';
                logger.log('m.room.guest_access 返回:', guestAccess);
            } catch {}
            try {
                isPublic = room.getJoinRule?.() === 'public';
            } catch {}
            // 获取头像
            let avatarUrl: string | null = null;
            try {
                avatarUrl = room.getAvatarUrl?.(client.baseUrl, 96, 96, 'crop') || null;
            } catch {}
            const settings: IRoomSettings = {
                name,
                topic: topic ?? '',
                avatarUrl,
                permissions: {
                    sendMessages: powerLevels.events_default || 0,
                    invite: powerLevels.invite || 50,
                    kick: powerLevels.kick || 50,
                    ban: powerLevels.ban || 50,
                    roomSettings: powerLevels.state_default || 50
                },
                privacy: {
                    isPublic,
                    isEncrypted,
                    allowGuestJoin
                }
            };
            logger.log('获取到的房间设置:', settings);
            logger.pause = true; // 打印完毕后关闭
            return settings;
        } catch (error) {
            logger.error('获取房间设置失败:', error);
            throw error;
        }
    },
}; 